import React from "react";
import { motion } from "framer-motion";
import { Sparkles as SparkIcon, BookOpen, ArrowDown } from "lucide-react";
import { IMAGES } from "../lib/content";
import Sparkles from "./Sparkles";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden flex items-center isolate"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${IMAGES.hero})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Subtle legibility overlays (preserve enchanted forest) */}
      <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#08140B]/90 via-[#08140B]/35 to-transparent" />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#08140B]/30 via-transparent to-[#08140B]" />

      <Sparkles count={50} />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 w-full pt-32 pb-20">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/30 bg-emerald-950/40 backdrop-blur-md mb-7"
          >
            <SparkIcon size={14} className="text-amber-300" />
            <span className="font-accent text-amber-200 text-base tracking-wide">
              A book series by Sybelle Cheesman
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white leading-[1.02] hero-text-shadow tracking-tight"
            data-testid="hero-title"
          >
            The Carmel
            <br />
            <span className="text-gradient-gold italic font-medium">
              Prince of Miara Mountain
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-8 text-lg sm:text-xl text-emerald-50/85 max-w-2xl leading-relaxed hero-text-shadow"
          >
            Slip through the hidden door at the foot of the mountain. Beyond it
            lies an undiscovered land of pixie fairies, dew-lanterns, and a tiny
            prince with a golden tiara — waiting to take you on the journey of a
            lifetime.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <button
              onClick={() => scrollTo("books")}
              className="btn-gold"
              data-testid="hero-cta-explore"
            >
              <BookOpen size={18} /> Discover the Books
            </button>
            <button
              onClick={() => scrollTo("characters")}
              className="btn-ghost"
              data-testid="hero-cta-characters"
            >
              Meet the Pixies
            </button>
          </motion.div>
        </div>
      </div>

      <motion.button
        onClick={() => scrollTo("books")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ delay: 1.2, duration: 2, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-amber-200/80 flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em]"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
      >
        Wander Deeper
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
};

export default Hero;
